import { EditOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Empty, Flex } from 'antd';
import { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type UserInfo, useGetOrganizationListOrganizationListGetQuery } from '@shared/api';
import { Routes } from '@shared/constants/routes';
import { hasRequiredPermission } from '@shared/lib/permissions';
import { Text } from '@shared/ui/antd';

import { CompanyEditModal } from './company-edit-modal';
import type { ProfileCompany } from '../types';
import styles from './companies.module.scss';

interface CompaniesProps {
  user?: UserInfo;
}

export const Companies: FC<CompaniesProps> = ({ user }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'profile.user.companies',
  });
  const navigate = useNavigate();
  const [editingCompany, setEditingCompany] = useState<ProfileCompany>();
  const { data: organizationDetails = [] } = useGetOrganizationListOrganizationListGetQuery();

  const companies = useMemo<ProfileCompany[]>(() => {
    const detailsById = new Map(
      organizationDetails.map((organization) => [organization.id, organization]),
    );

    return (user?.organizations ?? []).map((organization) => {
      const details = detailsById.get(organization.id);

      return {
        id: organization.id,
        title: organization.title,
        permission: organization.permission,
        email: details?.email,
        phone: details?.phone,
      };
    });
  }, [organizationDetails, user?.organizations]);

  const hasCompanies = companies.length > 0;

  return (
    <>
      <Flex vertical gap={12} className={styles.root} align="center" justify="center">
        {!hasCompanies && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} />}

        {companies.map((company) => {
          const canEdit = hasRequiredPermission(company.permission, 'ADMIN');

          return (
            <Flex key={company.id} vertical gap={16} className={styles.card}>
              <Flex align="center" justify="space-between" gap={16} wrap>
                <Flex vertical gap={4}>
                  <Text variant="lg" fontWeight={500}>
                    {company.title}
                  </Text>

                  <Text variant="xs" className={styles.role}>
                    {t('role', { role: t(`roles.${company.permission}`) })}
                  </Text>
                </Flex>

                {canEdit && (
                  <Button icon={<EditOutlined />} onClick={() => setEditingCompany(company)}>
                    {t('edit')}
                  </Button>
                )}
              </Flex>

              {(company.email || company.phone) && (
                <Flex vertical gap={12}>
                  {company.email && (
                    <Flex align="center" gap={12} className={styles.contact}>
                      <MailOutlined />
                      <Text variant="sm">{company.email}</Text>
                    </Flex>
                  )}

                  {company.phone && (
                    <Flex align="center" gap={12} className={styles.contact}>
                      <PhoneOutlined />
                      <Text variant="sm">{company.phone}</Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          );
        })}

        <Button
          type="primary"
          className={styles.createButton}
          onClick={() => navigate(Routes.profile.organization.root)}
          size="large"
        >
          {t('create')}
        </Button>
      </Flex>

      <CompanyEditModal
        company={editingCompany}
        open={Boolean(editingCompany)}
        onClose={() => setEditingCompany(undefined)}
      />
    </>
  );
};
